import { useEffect, useCallback, useState } from 'react';
import { BigNumber } from 'ethers';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';

import { notify } from '@lib/bugsnag';
import useWallets from '@hooks/useWallets';
import { Mode } from '@components/MarketsPageView/types';
import useSdkAndSupportedAssets from '@hooks/useSdkAndSupportedAssets';

interface YourBorrowCardState {
  amount: BigNumber | null;
  borrowRatePerBlock: BigNumber | null;
  loadingAmount: boolean;
  loadingBorrowRatePerBlock: boolean;
}

const initState: YourBorrowCardState = {
  amount: null,
  borrowRatePerBlock: null,
  loadingAmount: true,
  loadingBorrowRatePerBlock: true,
};

interface UseYourBorrowCardDataResult {
  data: {
    amount: YourBorrowCardState['amount'];
    borrowRatePerBlock: YourBorrowCardState['borrowRatePerBlock'];
  };
  loading: boolean;
  refetch: () => void;
}

function useYourBorrowCardData(
  asset: TectonicAsset | null,
  mode: Mode
): UseYourBorrowCardDataResult {
  const { sdk } = useSdkAndSupportedAssets(mode);
  const [data, setData] = useState<YourBorrowCardState>(initState);
  const { currentAccount: borrowerAddress } = useWallets();

  const getBorrowAmount = useCallback(
    async (a: TectonicAsset): Promise<void> => {
      if (borrowerAddress) {
        try {
          const amount = await sdk.Borrow.borrowedAmountOfUser(
            a.tTokenAddress,
            borrowerAddress
          );

          setData((v) => ({ ...v, amount, loadingAmount: false }));
        } catch (error) {
          notify(error);
          setData((v) => ({ ...v, loadingAmount: false }));
        }
      }
    },
    [borrowerAddress, sdk]
  );

  const getBorrowRatePerBlock = useCallback(
    async (a: TectonicAsset): Promise<void> => {
      if (a) {
        try {
          const borrowRatePerBlock = await sdk.Borrow.borrowRatePerBlock(
            a.tTokenAddress
          );
          setData((v) => ({
            ...v,
            borrowRatePerBlock,
            loadingBorrowRatePerBlock: false,
          }));
        } catch (error) {
          notify(error);
          setData((v) => ({
            ...v,
            loadingBorrowRatePerBlock: false,
          }));
        }
      }
    },
    [sdk.Borrow]
  );

  const refetch = useCallback(() => {
    if (asset) {
      getBorrowAmount(asset);
      getBorrowRatePerBlock(asset);
    }
  }, [asset, getBorrowAmount, getBorrowRatePerBlock]);

  useEffect(() => {
    if (asset) {
      getBorrowAmount(asset);
      getBorrowRatePerBlock(asset);
    }
  }, [asset, getBorrowAmount, getBorrowRatePerBlock]);

  return {
    data: { amount: data.amount, borrowRatePerBlock: data.borrowRatePerBlock },
    loading: data.loadingAmount || data.loadingBorrowRatePerBlock,
    refetch,
  };
}

export default useYourBorrowCardData;
